/**
 * Reconciliation and the month close — `/v1/admin/recon/*`.
 *
 * **Nothing here re-derives `is_clean`, an age or a threshold.** The server decides whether a day
 * counts as clean (every pass ran AND every break is closed), how old a difference is (from
 * `first_seen_at`, which never moves) and how many days is too many. A console keeping its own
 * copy of any of the three would be wrong and confident the first time ops moved a dial, and its
 * number would disagree with the escalation that actually fired.
 *
 * **Every write carries an `Idempotency-Key` minted when its dialog opened.** Accepting a break
 * and signing a month both close something permanently with nothing having moved to explain it; a
 * retried accept would name two different seconders on one decision, and a retried sign would be
 * a second freeze of a frozen period.
 *
 * **There is no hook that marks a day clean, and there cannot be one.** Clean is computed, not
 * set — a route that set it would let one person silence the streak that gates automatic payouts.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { idempotent } from '../idempotency';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type ReconDay = components['schemas']['ReconDayResponse'];
export type ReconRunRow = components['schemas']['ReconRunRow'];
export type ReconBreakRow = components['schemas']['ReconBreakRow'];
export type AgedBreakRow = components['schemas']['AgedBreakRow'];
export type MonthCloseDetail = components['schemas']['MonthCloseDetail'];
export type MonthCloseRow = components['schemas']['MonthCloseRow'];
export type ChecklistItemRow = components['schemas']['ChecklistItemRow'];
type BreakList = components['schemas']['ReconBreakListResponse'];
export type GatewayFeeRow = components['schemas']['GatewayFeeRow'];
export type GatewayFeeIn = components['schemas']['GatewayFeeIn'];
type GatewayFeeList = components['schemas']['GatewayFeeListResponse'];
type AgingList = components['schemas']['AgedBreakListResponse'];

export interface BreakQuery extends Record<string, unknown> {
  page: number;
  page_size: number;
  break_status?: string;
}

function search(params: BreakQuery): string {
  const query = new URLSearchParams();
  query.set('page', String(params.page));
  query.set('page_size', String(params.page_size));
  if (params.break_status) query.set('break_status', params.break_status);
  return query.toString();
}

export function useReconDay(day: string) {
  return useQuery({
    queryKey: keys.recon.day(day),
    queryFn: () => authedClient.request<ReconDay>(`/v1/admin/recon/days/${day}`),
  });
}

/** Re-runs every pass for one ended IST day, the way the nightly job does (R114). It cannot make a
 * day clean by itself: a pass with no source still records `failed`. */
export function useRunReconDay(day: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { reason: string; key: string }) =>
      authedClient.request<ReconDay>(`/v1/admin/recon/days/${day}/run`, {
        method: 'POST',
        body: { reason: input.reason },
        headers: idempotent(input.key),
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.recon.all() }),
  });
}

export function useReconBreaks(params: BreakQuery) {
  return useQuery({
    queryKey: keys.recon.breaks(params),
    queryFn: () => authedClient.request<BreakList>(`/v1/admin/recon/breaks?${search(params)}`),
  });
}

export function useBreakAging() {
  return useQuery({
    queryKey: keys.recon.aging(),
    queryFn: () => authedClient.request<AgingList>('/v1/admin/recon/breaks/aging'),
  });
}

export function useMonthClose(period: string) {
  return useQuery({
    queryKey: keys.recon.close(period),
    queryFn: () => authedClient.request<MonthCloseDetail>(`/v1/admin/recon/close/${period}`),
  });
}

export function useResolveBreak() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { breakId: number; kind: string; note?: string; key: string }) =>
      authedClient.request<ReconBreakRow>(`/v1/admin/recon/breaks/${input.breakId}/resolve`, {
        method: 'POST',
        body: { kind: input.kind, note: input.note },
        headers: idempotent(input.key),
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.recon.all() }),
  });
}

export function useAcceptBreak() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { breakId: number; seconded_by_admin_id: number; note: string; key: string }) =>
      authedClient.request<ReconBreakRow>(`/v1/admin/recon/breaks/${input.breakId}/accept`, {
        method: 'POST',
        body: { seconded_by_admin_id: input.seconded_by_admin_id, note: input.note },
        headers: idempotent(input.key),
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.recon.all() }),
  });
}

export function useCheckItem(period: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { code: string; evidence: string; key: string }) =>
      authedClient.request<ChecklistItemRow>(
        `/v1/admin/recon/close/${period}/items/${input.code}/check`,
        { method: 'POST', body: { evidence: input.evidence }, headers: idempotent(input.key) },
      ),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.recon.close(period) }),
  });
}

export function useWaiveItem(period: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { code: string; reason: string; key: string }) =>
      authedClient.request<ChecklistItemRow>(
        `/v1/admin/recon/close/${period}/items/${input.code}/waive`,
        { method: 'POST', body: { reason: input.reason }, headers: idempotent(input.key) },
      ),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.recon.close(period) }),
  });
}

export function useSignClose(period: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { founder_admin_id: number; key: string }) =>
      authedClient.request<MonthCloseRow>(`/v1/admin/recon/close/${period}/sign`, {
        method: 'POST',
        body: { founder_admin_id: input.founder_admin_id },
        headers: idempotent(input.key),
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.recon.all() }),
  });
}

/**
 * The written fee quote the passes check a provider against (R172; 08-payments/07 §6). Versioned:
 * a new line for an open schedule closes its predecessor the day before. Nothing is booked from
 * it, so one finance admin adds a line, audited, with no second approver.
 */
export function useGatewayFees() {
  return useQuery({
    queryKey: keys.recon.fees(),
    queryFn: () => authedClient.request<GatewayFeeList>('/v1/admin/recon/gateway-fees'),
  });
}

export function useAddGatewayFee() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ key, ...body }: GatewayFeeIn & { key: string }) =>
      authedClient.request<GatewayFeeRow>('/v1/admin/recon/gateway-fees', {
        method: 'POST',
        body,
        headers: idempotent(key),
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.recon.fees() }),
  });
}
